import React, { useEffect, useMemo } from "react";
import { Flex, Typography, Pagination, Spin, Empty } from "antd";
import ImageCard from "./ImageCard";
import "./index.css";
import { useAppDispatch, useAppSelector } from "../../hooks";
import {
  useListCatalogQuery,
  usePrefetch,
  useInvalidateCatalogMutation,
} from "../../actions/catalogInQuery";
import { useListDevicesQuery } from "../../actions/devicesInQuery";
import { useListTrapSitesQuery } from "../../actions/trapsiteInQuery";
import { setFilters } from "../../reducers/catalogFilterSlice";
import { useDebounce } from "../../hooks/useDebounce";
import {
  parseSpeciesToList,
  generateUniqueCatalogId,
} from "../../utils/helpers";

const { Text } = Typography;

const Catalog: React.FC = () => {
  const dispatch = useAppDispatch();
  const filter = useAppSelector((state) => state.catalogFilter);
  const debouncedFilter = useDebounce(filter, 300);

  const currentPage = filter.page + 1;
  const pageSize = 8;

  const queryParams = useMemo(() => {
    const { species: _species, ...rest } = debouncedFilter;
    return {
      ...rest,
      species: parseSpeciesToList(debouncedFilter.species),
      imageTypes: Object.entries(debouncedFilter.imageTypes)
        .filter(([_, checked]) => checked)
        .map(([key]) => key),
      pageSize,
    };
  }, [debouncedFilter, pageSize]);

  const { data, isLoading, isError, error } = useListCatalogQuery(queryParams);
  const [invalidateCatalog] = useInvalidateCatalogMutation();

  const { data: deviceData } = useListDevicesQuery();
  const { data: trapSiteData } = useListTrapSitesQuery();

  const deviceMap = useMemo(() => {
    return (
      deviceData?.items.reduce(
        (acc, device) => {
          if (device.uuid) {
            acc[device.uuid] = device.display_name || "";
          }
          return acc;
        },
        {} as Record<string, string>,
      ) || {}
    );
  }, [deviceData]);

  const trapSiteMap = useMemo(() => {
    return (
      trapSiteData?.items.reduce(
        (acc, trapSite) => {
          if (trapSite._id) {
            acc[trapSite._id] = trapSite.display_name || "";
          }
          return acc;
        },
        {} as Record<string, string>,
      ) || {}
    );
  }, [trapSiteData]);

  const { page: _page, ...filtersOnly } = debouncedFilter;
  const filtersOnlyString = JSON.stringify(filtersOnly);
  const prevFiltersRef = React.useRef(filtersOnlyString);

  useEffect(() => {
    // Invalidate cache when debounced filters (excluding page) change
    if (prevFiltersRef.current !== filtersOnlyString) {
      invalidateCatalog();
      prevFiltersRef.current = filtersOnlyString;
    }
  }, [filtersOnlyString, invalidateCatalog]);

  const prefetchPage = usePrefetch("listCatalog");

  useEffect(() => {
    // Prefetch next 2 pages based on debounced filter
    if (data?.count) {
      const totalPages = Math.ceil(data.count / pageSize);
      const currentDebouncedPage = debouncedFilter.page + 1;

      for (let i = 1; i <= 2; i++) {
        const nextPage = currentDebouncedPage + i;
        if (nextPage <= totalPages) {
          prefetchPage({
            ...queryParams,
            page: nextPage - 1, // API uses 0-based page index
          });
        }
      }
    }
  }, [data?.count, queryParams, debouncedFilter.page, prefetchPage, pageSize]);

  const handlePageChange = (page: number) => {
    dispatch(setFilters({ page: page - 1 }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (isLoading) {
    return (
      <Flex justify="center" align="center" className="h-full">
        <Spin size="large" />
      </Flex>
    );
  }

  if (isError) {
    return (
      <Flex justify="center" align="center" className="h-full">
        <Text type="danger">
          Failed to load catalog.{" "}
          {(error as any)?.data?.message || "Please try again later."}
        </Text>
      </Flex>
    );
  }

  const paginatedData = data?.items || [];
  const totalCount = data?.count || 0;

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 overflow-y-auto mb-6">
        {paginatedData.length > 0 ? (
          <Flex wrap="wrap" gap="large" justify="start">
            {paginatedData.map((item, index) => {
              return (
                <div
                  key={`catalog-${index}`}
                  className="w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.33%-16px)] xl:w-[calc(25%-18px)]"
                >
                  <ImageCard
                    {...item}
                    device_name={item.device ? deviceMap[item.device] : ""}
                    trap_site_name={
                      item.trap_site_id ? trapSiteMap[item.trap_site_id] : ""
                    }
                    catalogId={generateUniqueCatalogId(item?.source, item?.id)}
                    queryParams={queryParams}
                  />
                </div>
              );
            })}
          </Flex>
        ) : (
          <Flex justify="center" align="center" className="h-full">
            <Empty description="No images found" />
          </Flex>
        )}
      </div>

      <Flex justify="space-between" align="center" className="mt-auto py-4">
        <Text>
          {/* Showing {paginatedData?.length} of {totalCount} results */}
        </Text>
        <Pagination
          current={currentPage}
          pageSize={pageSize}
          total={totalCount}
          onChange={handlePageChange}
          showSizeChanger={false}
          className="catalog-pagination"
        />
      </Flex>
    </div>
  );
};

export default Catalog;
